import React, {useEffect, useMemo, useState} from "react";
import {Badge, Button, Checkbox, Col, Row, Space, Switch, Tooltip} from "antd";
import {
    AddIcon,
    DiamondIcon,
    DollarIcon,
    FlameIcon,
    StorageBoxOutlineIcon,
    WishlistIcon
} from "@components/icons/Icons";
import BookCover from "@components/book-cover";
import {formatMoney, formatPercent} from "@lib/format";
import {useWishlistAddIssueMutation, useWishlistRemoveIssueMutation} from "@mwServices/User";
import {showResponse} from "@services/Api";
import {IssueRenderProps} from "@components/grid/IssueGrid";
import useIsLoggedIn from "@hooks/useIsLoggedIn";
import AddComicModal from "@components/add-comic-modal";
import moment from "moment";
import {Link} from "react-router-dom";
import {IssueGradeTypes} from "@lib/constants";
import {KeyIssueBadge} from "@components/key-issue-badge";
import {getMyCollectionUrlWithIssueId} from "@lib/Routes";

export interface NewReleaseGridItemProps extends IssueRenderProps {
    hideDetails?: boolean
    showValueRibbon?: boolean;
    showDesktopView?: boolean;
    windowDimensions: any;
    showEditMultiple?: boolean;
    isSelected?: boolean;
    toggleSelected?: (issueId: number) => void;
}

const NewReleaseGridItem = ({
        item,
        onIssueUpdate,
        hideDetails,
        showValueRibbon,
        showDesktopView,
        windowDimensions ,
        showEditMultiple,
        isSelected,
        toggleSelected,
}: NewReleaseGridItemProps) => {
    const isLoggedIn = useIsLoggedIn();
    const [wishlistIssueAdd, addWishlistResponse] = useWishlistAddIssueMutation();
    const [wishlistIssueRemove, removeWishlistResponse] = useWishlistRemoveIssueMutation();
    const [addComic, setAddComic] = useState(null);
    const issueDate = useMemo(() => { return moment(item.date.split(' ').shift()).toDate(); }, [item]);

    const gradedOrSlab = useMemo(() => {
        const gradedOrRawUpperCase = item.graded_or_raw.toUpperCase();
        return gradedOrRawUpperCase == IssueGradeTypes.GRADED ? IssueGradeTypes.SLAB : gradedOrRawUpperCase;
    }, [item.graded_or_raw]);

    useEffect(() => {
        if (addWishlistResponse.isSuccess && onIssueUpdate) onIssueUpdate({ ...item, in_wishlist: true });
        showResponse(addWishlistResponse, 'Issue added to wishlist.', 'Failed to add issue to wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addWishlistResponse]);

    useEffect(() => {
        if (removeWishlistResponse.isSuccess && onIssueUpdate) onIssueUpdate({ ...item, in_wishlist: false });
        showResponse(removeWishlistResponse, 'Issue removed from wishlist.', 'Failed to remove issue from wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeWishlistResponse]);

    return (
        <>
            <div className="covr-grid-header">
                <Row justify="space-between">
                    {(showEditMultiple && toggleSelected) && (
                        <Col span={1} className="col-cbx">
                            <Checkbox
                                checked={isSelected}
                                onChange={() => toggleSelected(item.id)}
                            />
                        </Col>
                    )}
                    <span className="min-height-24">
                        {/*Todo: Put back in when pull lists are made.*/}
                        {false && !item?.isVariant && (
                            <Switch loading={false} unCheckedChildren="PULL" checkedChildren="PULLING"/>
                        )}
                    </span>
                    {isLoggedIn && item.total_in_collection > 0 && (
                        <Link className={"mar-top-5"} to={getMyCollectionUrlWithIssueId(item.id)}>
                            <span className="issues-owned">
                                <StorageBoxOutlineIcon/><strong>&nbsp;:&nbsp;<span
                                className="total-number">{item.total_in_collection}</span></strong>
                            </span>
                        </Link>
                    )}
                </Row>
            </div>
            <div className="covr-grid-content">
                <a href={`/issue/${item.slug}`}>
                    <BookCover issue={item} preview={false} text={item.variant_name}/>
                </a>
            </div>
            <div className="covr-grid-footer">
                <Row justify="center">
                    <Col span={24}>
                    <Row className="issue-grid-book-cover-value-white"
                             style={{borderBottomWidth: showValueRibbon ? 0 : 1}}>
                            <Col span={12}>
                                <span className="issueNumber">{`${item.issue_number}`}</span>
                                <span className="issueYear">({issueDate.getFullYear()})</span>
                            </Col>
                            <Col span={12} className="valueBadges">
                                {item.is_key && (
                                    <KeyIssueBadge keyInfo={item.key_info} windowDimensions={windowDimensions}
                                                   isLargeScreen={showDesktopView}/>)}
                                {item.is_hot && (<div className="valueBadge hotIssue isActive"><FlameIcon/></div>)}
                                {item.is_rare && (<div className="valueBadge rareIssue isActive"><DiamondIcon/></div>)}
                            </Col>
                        </Row>
                        {showValueRibbon && (
                            <Row
                                className={`issue-grid-book-cover-value ${gradedOrSlab == IssueGradeTypes.SLAB ? 'slabbed' : 'grey'}`}>
                                <Col span={18}>
                                    <span className="issueGrade">{gradedOrSlab} {item.most_common_grade} | </span>
                                    <span className="issueValue">{formatMoney(item.value)}</span>
                                </Col>
                                {item.trend_percent !== null && item.trend_percent !== 0 && (
                                    <Col span={6}
                                         className={"valueChange " + (item.trend_percent && item.trend_percent >= 0 ? 'up' : 'down')}>
                                        {formatPercent(item.trend_percent, true)}
                                    </Col>
                                )}
                            </Row>
                        )}
                        {!hideDetails && (
                            <Row className="issue-grid-book-cover-details">
                                <a className="title text-black" href={"/series/"+item.series_slug}>
                                    {item.series_title}
                                </a>
                                <p className="details">
                                    {!item.is_variant ? (<>{formatMoney(item.cover_price)}</>) : '--'}
                                </p>
                            </Row>
                        )}
                        <Row className="issue-actions" justify="center">
                            <Space>
                                {isLoggedIn && (
                                    <>
                                        <Tooltip title={item.in_wishlist ? "Remove from Wishlist" : "Add to Wishlist"}>
                                            <Button
                                                className={"issueButton greyButton " + (item.in_wishlist ? 'selected' : '')}
                                                shape="circle" onClick={() => {
                                                if (item.in_wishlist) {
                                                    wishlistIssueRemove([item.id]);
                                                } else {
                                                    wishlistIssueAdd([item.id]);
                                                }
                                            }}>
                                                <WishlistIcon/>
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Add to Collection">
                                            <Button className="issueButton greyButton" shape="circle" size="large"
                                                    onClick={() => {
                                                        setAddComic(item);
                                                    }}>
                                                <AddIcon/>
                                            </Button>
                                        </Tooltip>
                                    </>
                                )}
                                <Tooltip title="Buy In Marketplace">
                                    <a href={`/marketplace/?issue_id=${item.id}`}>
                                        <Button className="issueButton greyButton" shape="circle">
                                            <DollarIcon className="dollar"/>
                                        </Button>
                                    </a>
                                </Tooltip>
                            </Space>
                        </Row>
                    </Col>
                </Row>
            </div>

            {addComic && (
                <AddComicModal isModalOpen={true} issue={addComic} onCancel={() => setAddComic(null)}
                               onIssueUpdate={onIssueUpdate}/>
            )}
        </>
    );
};

export default NewReleaseGridItem;